import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';
import { AddressFormLocs } from 'libs/cm/ui/src/lib/components/cm-address-form/address-form.type';

type CPProfileAddressFormLocs =
	| 'CPProfileAddressFormMailingTitle'
	| 'CPProfileAddressFormLocalizedTitle'
	| 'CPProfileAddressFormOtherTitle'
	| 'CPProfileAddressFormUpdateRequired'
	| 'CPProfileAddressFormLabelCountry'
	| 'CPProfileAddressFormErrorCountryRequired'
	| 'CPProfileAddressFormErrorInvalidCountry'
	| 'CPProfileAddressFormLabelAddress'
	| 'CPProfileAddressFormLabelCity'
	| 'CPProfileAddressFormLabelState'
	| 'CPProfileAddressFormLabelPostal'
	| 'CPProfileAddressFormLabelPhone'
	| 'CPProfileAddressFormLabelFax'
	| 'CPProfileAddressFormErrorPhoneRequired'
	| 'CPProfileAddressFormErrorPhoneVueUsaCanada'
	| 'CPProfileAddressFormErrorPhoneVueNoZeros'
	| 'CPProfileAddressFormErrorPostalRequired'
	| 'CPProfileAddressFormErrorPostalVueUSA'
	| 'CPProfileAddressFormErrorPostalVueCanada'
	| 'CPProfileAddressFormErrorPostalMaxLength'
	| 'CPProfileAddressFormErrorStateRequired'
	| 'CPProfileAddressFormErrorStateRequiredVueHint'
	| 'CPProfileAddressFormErrorStateInvalid'
	| 'CPProfileAddressFormErrorCityRequired'
	| 'CPProfileAddressFormLabelAddressContinued1'
	| 'CPProfileAddressFormLabelAddressContinued2'
	| 'CPProfileAddressFormLabelBusinessAddress'
	| 'CPProfileAddressFormButtonUpdateAddress'
	| 'CPProfileAddressFormButtonCancel'
	| 'CPProfileAddressFormErrorAddressRequired'
	| 'CPProfileAddressFormHintCountry'
	| 'CPProfileAddressFormHintAddress';

const dpRoot = 'Profile|Addresses';

const sharedLocDefs: Pick<LocDef, 'feature' | 'portal' | 'htmlAllowed' | 'shared'> = {
	feature: HubFeature.CandidateProfileAddresses,
	htmlAllowed: false,
	shared: false,
	portal: 'CP' as LocPortal,
};

export const cpProfileAddressFormLocs: LocDefs<CPProfileAddressFormLocs> = {
	CPProfileAddressFormMailingTitle: {
		legacyName: 'CandidateProfile.Addresses.Primary/Profile.Addresses.Title.MailingAddress',
		defaultValue: 'Mailing Address',
		description: 'Title for mailing address.',
		displayPath: `${dpRoot}|Mailing Address`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPProfileAddressFormLocalizedTitle: {
		legacyName: 'CandidateProfile.Addresses.Localized/Profile.Addresses.Title.LocalizedAddress',
		defaultValue: 'Localized Address',
		description: 'Title for localized address.',
		displayPath: `${dpRoot}|Localized Address`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPProfileAddressFormOtherTitle: {
		legacyName: 'CandidateProfile.Addresses.Secondary/Profile.Addresses.Title.OtherAddress',
		defaultValue: 'Other Address',
		description: 'Title for other address.',
		displayPath: `${dpRoot}|Other Address`,
		displayType: 'Title',
		...sharedLocDefs,
	},
	CPProfileAddressFormUpdateRequired: {
		legacyName:
			'CandidateProfile.Addresses.Primary.RequireMinimum/System.Prerequisite.PrimaryAddressUpdateRequired',
		defaultValue:
			'Your mailing address information is incomplete and must be filled out before continuing to use this site.',
		description: 'Error message stating that primary address update is required.',
		displayPath: `${dpRoot}|Update Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address',
		defaultValue: 'Address',
		description: 'Label for address.',
		displayPath: `${dpRoot}|Address`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelAddressContinued1: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address2',
		defaultValue: 'Address (cont.)',
		description: 'Label for address second line.',
		displayPath: `${dpRoot}|Address (line 2)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelAddressContinued2: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address3',
		defaultValue: 'Address (cont.)',
		description: 'Label for address third line.',
		displayPath: `${dpRoot}|Address (line 3)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelBusinessAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Business',
		defaultValue: 'This is a Business Address',
		description: 'Label for the business address checkbox.',
		displayPath: `${dpRoot}|Is Business`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelCity: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.City',
		defaultValue: 'City',
		description: 'Error saying the city is required.',
		displayPath: `${dpRoot}|City`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Country',
		defaultValue: 'Country',
		description: 'Label for country.',
		displayPath: `${dpRoot}|Country`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelFax: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Fax',
		defaultValue: 'Fax',
		description: 'Label for fax.',
		displayPath: `${dpRoot}|Fax`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelPhone: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Phone',
		defaultValue: 'Phone',
		description: 'Label for phone.',
		displayPath: `${dpRoot}|Phone`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelPostal: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.PostalCode',
		defaultValue: 'Zip/Postal Code',
		description: 'Label for zip.',
		displayPath: `${dpRoot}|Zip/Postal Code`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormLabelState: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.State',
		defaultValue: 'State/Province',
		description: 'Label for state.',
		displayPath: `${dpRoot}|State`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorAddressRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.AddressRequired',
		defaultValue: 'Address is required',
		description: 'Error saying the address is required.',
		displayPath: `${dpRoot}|Address Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorCityRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CityRequired',
		defaultValue: 'City is required',
		description: 'Error saying city is required.',
		displayPath: `${dpRoot}|City Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorCountryRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CountryRequired',
		defaultValue: 'Country is required',
		description: 'Error saying country is required.',
		displayPath: `${dpRoot}|Country Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorInvalidCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.InvalidCountry',
		defaultValue: 'Invalid country',
		description: 'Error saying the selected country is invalid.',
		displayPath: `${dpRoot}|Invalid Country`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorStateInvalid: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateInvalid',
		defaultValue: 'Invalid state/province',
		description: 'Error saying the selected state is invalid.',
		displayPath: `${dpRoot}|Invalid State`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorStateRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateRequired',
		defaultValue: 'State/Province is required',
		description: 'Error saying state is required.',
		displayPath: `${dpRoot}|State Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormErrorStateRequiredVueHint: {
		defaultValue: 'State is required. If your state is not listed, please type N/A',
		description: 'Error with Vue specific hint for state required.',
		displayPath: `${dpRoot}|State Required Vue Hint`,
		displayType: 'Error',
		htmlAllowed: false,
		shared: false,
		portal: 'CP' as LocPortal,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPostalRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalRequired',
		defaultValue: 'Zip/Postal Code is required',
		description: 'Error saying the postal code is required.',
		displayPath: `${dpRoot}|Postal Code Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPostalVueUSA: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalUsa',
		defaultValue:
			'Zip/Postal Code must follow one of the two following formats: #####, #####-####',
		description:
			'Error saying the postal code must follow one of the two following formats: #####, #####-####.',
		displayPath: `${dpRoot}|Postal Code USA Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPostalVueCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalCanada',
		defaultValue: 'Zip/Postal Code must follow the following format: A#A#A#',
		description: 'Error saying the postal code must follow the following format: a#a#a#.',
		displayPath: `${dpRoot}|Postal Code Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPhoneRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneRequired',
		defaultValue: 'Phone number is required',
		description: 'Error saying the phone number is required.',
		displayPath: `${dpRoot}|Phone Number Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPhoneVueUsaCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneUsa',
		defaultValue: 'Phone number must have 10 numbers',
		description: 'Error saying the phone number must have 10 numbers.',
		displayPath: `${dpRoot}|Phone Number USA Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPhoneVueNoZeros: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneZeros',
		defaultValue: 'Phone number must not be all zeros',
		description: 'Error saying the phone number must not be all zeros.',
		displayPath: `${dpRoot}|Phone Number No Zeros`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	CPProfileAddressFormErrorPostalMaxLength: {
		defaultValue: 'Maximum number of allowed characters in postal code are: ',
		description: 'Error describing the maximum number of characters in the postal code.',
		displayPath: `${dpRoot}|Postal Code Length`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	CPProfileAddressFormButtonCancel: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.Cancel',
		defaultValue: 'Cancel',
		description:
			'Button to cancel editing of candidate addresses. This is shared between mailing address, localized address and the other address fields.',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPProfileAddressFormButtonUpdateAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.UpdateAddress',
		defaultValue: 'Update Address',
		description: 'Button to update the address.',
		displayPath: `${dpRoot}|Save`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	CPProfileAddressFormHintCountry: {
		defaultValue: 'To change your country, please contact support.',
		description: 'Hint for country.',
		displayPath: `${dpRoot}|Country Hint`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileRestrictCandidateCountryUpdates,
		htmlAllowed: true,
		shared: false,
		portal: 'CP' as LocPortal,
	},
	CPProfileAddressFormHintAddress: {
		defaultValue: '',
		description: 'Hint for address.',
		displayPath: `${dpRoot}|Address Hint`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileAddresses,
		htmlAllowed: false,
		shared: true,
		portal: 'CP' as LocPortal,
	},
};

export const PROFILE_ADDRESS_FORM_LOC_KEYS =
	MapLocKeys<CPProfileAddressFormLocs>(cpProfileAddressFormLocs);
export type CPProfileAddressFormLocKeys = LocMap<CPProfileAddressFormLocs>;

export const mappedAddressFormLocs: AddressFormLocs = {
	AddressFormLabelCountry: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelCountry,
	AddressFormLabelFax: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelFax,
	AddressFormErrorCountryRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorCountryRequired,
	AddressFormErrorInvalidCountry:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorInvalidCountry,
	AddressFormLabelAddress: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelAddress,
	AddressFormLabelCity: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelCity,
	AddressFormLabelState: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelState,
	AddressFormLabelPostal: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelPostal,
	AddressFormLabelPhone: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelPhone,
	AddressFormErrorPhoneRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPhoneRequired,
	AddressFormErrorPhoneVueUsaCanada:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPhoneVueUsaCanada,
	AddressFormErrorPhoneVueNoZeros:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPhoneVueNoZeros,
	AddressFormErrorPostalRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPostalRequired,
	AddressFormErrorPostalVueUSA:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPostalVueUSA,
	AddressFormErrorPostalVueCanada:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPostalVueCanada,
	AddressFormErrorPostalMaxLength:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorPostalMaxLength,
	AddressFormErrorStateRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorStateRequired,
	AddressFormErrorStateInvalid:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorStateInvalid,
	AddressFormErrorStateRequiredVueHint:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorStateRequiredVueHint,
	AddressFormErrorCityRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorCityRequired,
	AddressFormLabelAddressContinued2:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelAddressContinued1,
	AddressFormLabelAddressContinued3:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelAddressContinued2,
	AddressFormLabelBusinessAddress:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormLabelBusinessAddress,
	AddressFormButtonUpdateAddress:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormButtonUpdateAddress,
	AddressFormButtonCancel: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormButtonCancel,
	AddressFormErrorAddressRequired:
		PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormErrorAddressRequired,
	AddressFormHintCountry: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormHintCountry,
	AddressFormHintAddress: PROFILE_ADDRESS_FORM_LOC_KEYS.CPProfileAddressFormHintAddress,
};
